// externals
import { useSelector } from 'react-redux';
import React, { useState } from 'react';

// components
import { dealLockHooks, PaymentLoading } from '@makemydeal/dr-dash-components';
import { LoadingSectionParent } from '@makemydeal/dr-shared-components';
import { CardHeader } from '@interstate/components/Card';

import { Typography } from '@interstate/components/Typography';

import { Alert } from '@interstate/components/Alert';
import { TradeInDeleteUndoIcon } from './MultiTradeIn/TradeInDeleteUndoIcon';

// styling
import { TradeInSeparator, TradeInFormsContainer } from './trade.interstate.style';

// selectors
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as tradeInSelectors from '@makemydeal/dr-dash-store/selectors/trade';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';

// constants
import { StretchHeightCard } from '../deskingActivity/DeskingCard.style';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Box } from '@interstate/components/Box';
import { MultiTradeIn } from './MultiTradeIn/MultiTradeIn';
import { formatNumberAndCents } from '@makemydeal/dr-common-utils';

export interface TradeCardProps {
    isEditDisabled?: boolean;
    isTradeInFormDirtyRef?: React.MutableRefObject<boolean>;
    onContractingErrorsAlert?: (hasErrors: boolean) => void;
}

/* istanbul ignore next - default parameters rarely used */
const TradeCard = ({ isEditDisabled = false, isTradeInFormDirtyRef, onContractingErrorsAlert }: TradeCardProps) => {
    const [hasContractingErrors, setContractingErrors] = useState(false);
    const showVinDecodeError = useSelector(tradeInSelectors.getErrorDecodingVin);
    const isLocked = dealLockHooks.useIsDealLocked();
    const isGCDynamicValidationsEnabled = useSelector(featureToggleSelectors.isGCDynamicValidationsEnabled);
    const tradeIns = useSelector(tradeInsSelectors.getAllTradeIns);
    const theme = useInterstateTheme();
    const totalPayoff = useSelector(tradeInsSelectors.getIncludedTradeInsTotalAmountOwed);
    const totalAllowance = useSelector(tradeInsSelectors.getIncludedTradeInsTotalValue);

    const handleContractingErrorsAlert = (hasErrors: boolean) => {
        setContractingErrors(hasErrors);
        onContractingErrorsAlert?.(hasErrors);
    };

    return (
        <LoadingSectionParent>
            <PaymentLoading testId="trade-loading-indicator" />
            <StretchHeightCard
                data-testid="trade-card"
                containername="trade-card"
                className="stretch-height-card"
                header={
                    <CardHeader
                        title={
                            <Box>
                                <Box display="flex" justifyContent="space-between" alignItems="start">
                                    {tradeIns?.length === 1 && tradeIns[0] && (
                                        <TradeInDeleteUndoIcon
                                            formIndex={0}
                                            isDeleted={tradeIns[0].isDeleted}
                                            isEditDisabled={isEditDisabled || isLocked}
                                        />
                                    )}
                                </Box>
                                {tradeIns.length > 1 && (
                                    <Box
                                        display="flex"
                                        sx={{ gap: (theme) => `clamp(8px, 5vw, ${theme.spacing(20)})` }}
                                        paddingBottom="1rem"
                                        marginTop={0}
                                    >
                                        <Box display="flex" gap={1} data-testid="total-allowance">
                                            <Typography variant="body-md" tag="span" color="#6d6d6d">
                                                Total Allowance
                                            </Typography>
                                            <Typography variant="body-md" tag="span">
                                                ${formatNumberAndCents(totalAllowance)}
                                            </Typography>
                                        </Box>
                                        <Box display="flex" gap={1} data-testid="total-payoff">
                                            <Typography variant="body-md" tag="span" color="#6d6d6d">
                                                Total Payoff
                                            </Typography>
                                            <Typography variant="body-md" tag="span">
                                                ${formatNumberAndCents(totalPayoff)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                {tradeIns.length > 1 && <TradeInSeparator />}
                            </Box>
                        }
                    />
                }
                content={
                    <TradeInFormsContainer theme={theme}>
                        {showVinDecodeError && (
                            <Alert type="error" role="alert">
                                <Typography variant="body-sm" tag="span">
                                    There was a problem decoding the VIN. Please try again.
                                </Typography>
                            </Alert>
                        )}
                        {isGCDynamicValidationsEnabled && hasContractingErrors && (
                            <Alert type="error" sx={{ margin: '1rem 0' }}>
                                <Typography variant="body-sm" tag="span">
                                    Cannot generate contract. Please fix the highlighted Trade In details before continuing.
                                </Typography>
                            </Alert>
                        )}
                        <MultiTradeIn
                            isEditDisabled={isEditDisabled}
                            isTradeInFormDirtyRef={isTradeInFormDirtyRef}
                            onContractingErrorsAlert={handleContractingErrorsAlert}
                        />
                    </TradeInFormsContainer>
                }
            />
        </LoadingSectionParent>
    );
};

export default TradeCard;
